import React, { useState, useEffect } from 'react';
import { 
  LogOut, 
  FileText, 
  Calendar, 
  DollarSign, 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertCircle,
  Home,
  User,
  Settings,
  BarChart3,
  Menu,
  X,
  CreditCard,
  History
} from 'lucide-react';
import { useToast } from '../context/ToastContext';
import { Base_Url } from '../config/domainConfig';
import './UserPanel.css';

const UserPanel = () => {
  const { showError, showSuccess } = useToast();
  const [userData, setUserData] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [stats, setStats] = useState({
    totalApplications: 0,
    successfulPayments: 0,
    pendingPayments: 0,
    failedPayments: 0
  });

  const API_BASE_URL = `${Base_Url}/api`;

  useEffect(() => {
    // Get user data from localStorage
    const storedUserData = localStorage.getItem('userData');
    const token = localStorage.getItem('userToken');

    if (!storedUserData || !token) {
      showError('Please login to access your applications');
      window.location.href = '/login';
      return;
    }

    setUserData(JSON.parse(storedUserData));
    fetchUserApplications(token);
  }, []);

  const fetchUserApplications = async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/user-applications`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setApplications(data.data.applications);
        setStats({
          totalApplications: data.data.totalApplications,
          successfulPayments: data.data.successfulPayments,
          pendingPayments: data.data.pendingPayments,
          failedPayments: data.data.failedPayments
        });
      } else {
        showError(data.message || 'Failed to fetch applications');
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
      showError('Failed to fetch applications');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentHistory = async (applicationId) => {
    try {
      const token = localStorage.getItem('userToken');
      const response = await fetch(`${API_BASE_URL}/payments/history/${applicationId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setPaymentHistory(data.data.payments);
      } else {
        showError(data.message || 'Failed to fetch payment history');
      }
    } catch (error) {
      console.error('Error fetching payment history:', error);
      showError('Failed to fetch payment history');
    }
  };

  const handlePayNow = async (payment) => {
    setSelectedPayment(payment);
    setShowPaymentModal(true);
  };

  const handlePaymentSubmit = async () => {
    if (!selectedPayment) return;

    setPaymentLoading(true);
    try {
      const token = localStorage.getItem('userToken');
      
      // Create payment order
      const orderResponse = await fetch(`${API_BASE_URL}/payments/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          applicationId: selectedPayment.application_id,
          amount: selectedPayment.amount
        })
      });

      const orderData = await orderResponse.json();

      if (!orderData.success) {
        throw new Error(orderData.message || 'Failed to create payment order');
      }

      // Initialize Razorpay payment
      const options = {
        key: 'rzp_test_BWMg0dPw8Az8vc', // Your Razorpay key
        amount: orderData.data.amount,
        currency: orderData.data.currency,
        name: 'Vikas Fund',
        description: 'Additional Payment',
        order_id: orderData.data.orderId,
        handler: async function (response) {
          try {
            // Verify payment
            const verifyResponse = await fetch(`${API_BASE_URL}/payments/verify`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                applicationId: selectedPayment.application_id
              }),
            });
            
            const verifyData = await verifyResponse.json();
            
            if (verifyData.success) {
              showSuccess('Payment successful!');
              setShowPaymentModal(false);
              setSelectedPayment(null);
              // Refresh payment history
              await fetchPaymentHistory(selectedPayment.application_id);
              // Refresh applications
              await fetchUserApplications(token);
            } else {
              throw new Error(verifyData.message || 'Payment verification failed');
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            showError('Payment verification failed: ' + error.message);
          }
        },
        prefill: {
          name: userData?.name,
          email: userData?.email,
          contact: userData?.phone
        },
        theme: {
          color: '#667eea'
        }
      };
      
      const rzp = new window.Razorpay(options);
      rzp.open();
      
    } catch (error) {
      console.error('Payment error:', error);
      showError('Payment failed: ' + error.message);
    } finally {
      setPaymentLoading(false);
    }
  };

  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedPayment(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('userToken');
    localStorage.removeItem('userData');
    showSuccess('Logged out successfully');
    window.location.href = '/';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle size={16} className="status-icon approved" />;
      case 'rejected':
        return <XCircle size={16} className="status-icon rejected" />;
      case 'reviewing':
        return <Clock size={16} className="status-icon reviewing" />;
      default:
        return <AlertCircle size={16} className="status-icon pending" />;
    }
  };

  const getPaymentStatusIcon = (paymentStatus) => {
    switch (paymentStatus) {
      case 'success':
        return <CheckCircle size={16} className="status-icon approved" />;
      case 'failed':
        return <XCircle size={16} className="status-icon rejected" />;
      case 'pending':
        return <Clock size={16} className="status-icon reviewing" />;
      default:
        return <AlertCircle size={16} className="status-icon pending" />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleViewDetails = (application) => {
    setSelectedApplication(application);
    setShowDetailsModal(true);
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedApplication(null);
  };

  if (loading) {
    return (
      <div className="user-panel-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading your applications...</p>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <>
            {/* Statistics */}
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">
                  <FileText size={24} />
                </div>
                <div className="stat-content">
                  <h3>{stats.totalApplications}</h3>
                  <p>Total Applications</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon success">
                  <CheckCircle size={24} />
                </div>
                <div className="stat-content">
                  <h3>{stats.successfulPayments}</h3>
                  <p>Successful Payments</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon warning">
                  <Clock size={24} />
                </div>
                <div className="stat-content">
                  <h3>{stats.pendingPayments}</h3>
                  <p>Pending Payments</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon error">
                  <XCircle size={24} />
                </div>
                <div className="stat-content">
                  <h3>{stats.failedPayments}</h3>
                  <p>Failed Payments</p>
                </div>
              </div>
            </div>

            {/* Applications List */}
            <div className="applications-section">
              <h2>Your Applications</h2>
              {applications.length === 0 ? (
                <div className="no-applications">
                  <FileText size={48} />
                  <h3>No Applications Found</h3>
                  <p>You haven't submitted any applications yet.</p>
                  <button onClick={() => window.location.href = '/'} className="apply-btn">
                    Submit New Application
                  </button>
                </div>
              ) : (
                <div className="applications-grid">
                  {applications.map((app) => (
                    <div key={app.id} className="application-card">
                      <div className="app-header">
                        <h3>{app.name}</h3>
                        <div className="app-status">
                          {getStatusIcon(app.status)}
                          <span className={`status-text ${app.status}`}>
                            {app.status.charAt(0).toUpperCase() + app.status.slice(1)}
                          </span>
                        </div>
                      </div>

                      <div className="app-details">
                        <div className="detail-row">
                          <span className="label">Email:</span>
                          <span>{app.email}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Phone:</span>
                          <span>{app.phone}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Category:</span>
                          <span>{app.category}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Submitted:</span>
                          <span>{formatDate(app.created_at)}</span>
                        </div>
                      </div>

                      <div className="payment-info">
                        <div className="payment-status">
                          {getPaymentStatusIcon(app.payment_status)}
                          <span className={`payment-text ${app.payment_status || 'none'}`}>
                            {app.payment_status_display || 'No Payment'}
                          </span>
                        </div>
                        {app.payment_amount && (
                          <div className="payment-amount">
                            <DollarSign size={16} />
                            <span>₹{app.payment_amount}</span>
                          </div>
                        )}
                      </div>

                      {app.payment_status === 'pending' && app.payment_amount && (
                        <button
                          className="pay-now-btn"
                          onClick={async () => {
                            try {
                              // Fetch latest payment/order details
                              const token = localStorage.getItem('userToken');
                              const res = await fetch(`${API_BASE_URL}/payments/status/${app.id}`, {
                                headers: { 'Authorization': `Bearer ${token}` }
                              });
                              const paymentData = await res.json();
                              if (!paymentData.success || !paymentData.data.payments[0]) {
                                showError('Could not fetch payment details.');
                                return;
                              }
                              const latestPayment = paymentData.data.payments[0];
                              const options = {
                                key: 'rzp_test_BWMg0dPw8Az8vc',
                                amount: latestPayment.amount * 100,
                                currency: 'INR',
                                name: 'Vikas Fund',
                                description: 'Application Payment',
                                order_id: latestPayment.razorpay_order_id,
                                handler: async function (response) {
                                  console.log('Razorpay response:', response, app.id);
                                  const verifyBody = {
                                    razorpay_order_id: response.razorpay_order_id,
                                    razorpay_payment_id: response.razorpay_payment_id,
                                    razorpay_signature: response.razorpay_signature,
                                    applicationId: Number(app.id)
                                  };
                                  console.log('Verify body:', verifyBody);
                                  const verifyRes = await fetch(`${API_BASE_URL}/payments/verify`, {
                                    method: 'POST',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      'Authorization': `Bearer ${token}`
                                    },
                                    body: JSON.stringify(verifyBody)
                                  });
                                  const verifyData = await verifyRes.json();
                                  if (verifyData.success) {
                                    showSuccess('Payment successful!');
                                    fetchUserApplications(token);
                                  } else {
                                    showError('Payment verification failed: ' + verifyData.message);
                                  }
                                },
                                prefill: {
                                  name: app.name,
                                  email: app.email,
                                  contact: app.phone
                                },
                                theme: { color: '#667eea' }
                              };
                              const rzp = new window.Razorpay(options);
                              rzp.open();
                            } catch (err) {
                              showError('Error initiating payment.');
                            }
                          }}
                        >
                          Pay Now
                        </button>
                      )}

                      <div className="app-actions">
                        <button 
                          className="view-details-btn"
                          onClick={() => handleViewDetails(app)}
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        );
      case 'profile':
        return (
          <div className="profile-section">
            <h2>My Profile</h2>
            <div className="profile-card">
              <div className="profile-info">
                <div className="profile-avatar">
                  <User size={48} />
                </div>
                <div className="profile-details">
                  <h3>{userData?.name}</h3>
                  <p className="profile-email">{userData?.email}</p>
                  <div className="profile-stats">
                    <div className="profile-stat">
                      <span className="label">Phone:</span>
                      <span>{userData?.phone}</span>
                    </div>
                    <div className="profile-stat">
                      <span className="label">Member Since:</span>
                      <span>{formatDate(userData?.created_at || new Date())}</span>
                    </div>
                    <div className="profile-stat">
                      <span className="label">Total Applications:</span>
                      <span>{stats.totalApplications}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'payments':
        return (
          <div className="payments-section">
            <h2>Payment History</h2>
            {applications.length === 0 ? (
              <div className="no-payments">
                <CreditCard size={48} />
                <h3>No Applications Found</h3>
                <p>You need to submit an application to see payment history.</p>
                <button onClick={() => window.location.href = '/'} className="apply-btn">
                  Submit New Application
                </button>
              </div>
            ) : (
              <div className="applications-payment-list">
                {applications.map((app) => (
                  <div key={app.id} className="payment-application-card">
                    <div className="app-header">
                      <h3>{app.name}</h3>
                      <div className="app-status">
                        {getStatusIcon(app.status)}
                        <span className={`status-text ${app.status}`}>
                          {app.status.charAt(0).toUpperCase() + app.status.slice(1)}
                        </span>
                      </div>
                    </div>
                    
                    <div className="app-details">
                      <div className="detail-row">
                        <span className="label">Application ID:</span>
                        <span>#{app.id}</span>
                      </div>
                      <div className="detail-row">
                        <span className="label">Category:</span>
                        <span>{app.category}</span>
                      </div>
                      <div className="detail-row">
                        <span className="label">Submitted:</span>
                        <span>{formatDate(app.created_at)}</span>
                      </div>
                    </div>

                    <div className="payment-actions">
                      <button 
                        className="view-payments-btn"
                        onClick={() => fetchPaymentHistory(app.id)}
                      >
                        View Payment History
                      </button>
                    </div>

                    {/* Payment History for this application */}
                    {paymentHistory.length > 0 && paymentHistory[0]?.application_id === app.id && (
                      <div className="payment-history">
                        <h4>Payment History</h4>
                        <div className="payment-list">
                          {paymentHistory.map((payment) => (
                            <div key={payment.id} className="payment-item">
                              <div className="payment-info">
                                <div className="payment-status">
                                  {getPaymentStatusIcon(payment.status)}
                                  <span className={`payment-text ${payment.status}`}>
                                    {payment.status_display}
                                  </span>
                                </div>
                                <div className="payment-details">
                                  <div className="payment-amount">
                                    <DollarSign size={16} />
                                    <span>₹{payment.amount}</span>
                                  </div>
                                  <div className="payment-date">
                                    <Calendar size={16} />
                                    <span>{formatDate(payment.created_at)}</span>
                                  </div>
                                </div>
                                {payment.failure_reason && (
                                  <div className="failure-reason">
                                    <span className="label">Reason:</span>
                                    <span>{payment.failure_reason}</span>
                                  </div>
                                )}
                              </div>
                              <div className="payment-actions">
                                {payment.status === 'pending' && (
                                  <button 
                                    className="pay-now-btn"
                                    onClick={() => handlePayNow(payment)}
                                  >
                                    <CreditCard size={16} />
                                    Pay Now
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      case 'settings':
        return (
          <div className="settings-section">
            <h2>Settings</h2>
            <div className="settings-card">
              <h3>Account Settings</h3>
              <p>Settings functionality will be available soon.</p>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="user-panel-container">
      {/* Sidebar */}
      <div className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <h2>Vikas Fund</h2>
          <button 
            className="sidebar-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        <div className="sidebar-user">
          <div className="user-avatar">
            <User size={24} />
          </div>
          <div className="user-details">
            <h4>{userData?.name}</h4>
            <p>{userData?.email}</p>
          </div>
        </div>

        <nav className="sidebar-nav">
          <button 
            className={`nav-item ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            <Home size={20} />
            <span>Dashboard</span>
          </button>
          <button 
            className={`nav-item ${activeTab === 'payments' ? 'active' : ''}`}
            onClick={() => setActiveTab('payments')}
          >
            <History size={20} />
            <span>Payment History</span>
          </button>
          <button 
            className={`nav-item ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            <User size={20} />
            <span>Profile</span>
          </button>
          <button 
            className={`nav-item ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            <Settings size={20} />
            <span>Settings</span>
          </button>
        </nav>

        <div className="sidebar-footer">
          <button onClick={handleLogout} className="logout-btn">
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Mobile Header */}
        <div className="mobile-header">
          <button 
            className="mobile-menu-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <Menu size={24} />
          </button>
          <h1>Welcome, {userData?.name}!</h1>
        </div>

        {/* Content */}
        <div className="content-wrapper">
          {renderContent()}
        </div>
      </div>

      {/* Application Details Modal */}
      {showDetailsModal && selectedApplication && (
        <div className="modal-overlay" onClick={closeDetailsModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Application Details</h2>
              <button className="modal-close" onClick={closeDetailsModal}>
                <X size={24} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="application-details-grid">
                {/* Personal Information */}
                <div className="detail-section">
                  <h3>Personal Information</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="label">Full Name:</span>
                      <span className="value">{selectedApplication.name}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Email:</span>
                      <span className="value">{selectedApplication.email}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Phone:</span>
                      <span className="value">{selectedApplication.phone}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Address:</span>
                      <span className="value">{selectedApplication.address}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">City:</span>
                      <span className="value">{selectedApplication.city}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">State:</span>
                      <span className="value">{selectedApplication.state}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Country:</span>
                      <span className="value">{selectedApplication.country}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Occupation:</span>
                      <span className="value">{selectedApplication.occupation}</span>
                    </div>
                  </div>
                </div>

                {/* Business Information */}
                <div className="detail-section">
                  <h3>Business Information</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="label">Category:</span>
                      <span className="value">{selectedApplication.category}</span>
                    </div>
                    <div className="detail-item full-width">
                      <span className="label">Business Idea:</span>
                      <span className="value idea-text">{selectedApplication.idea}</span>
                    </div>
                  </div>
                </div>

                {/* Application Status */}
                <div className="detail-section">
                  <h3>Application Status</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="label">Status:</span>
                      <span className={`value status-badge ${selectedApplication.status}`}>
                        {getStatusIcon(selectedApplication.status)}
                        {selectedApplication.status.charAt(0).toUpperCase() + selectedApplication.status.slice(1)}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Submitted:</span>
                      <span className="value">{formatDate(selectedApplication.created_at)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Last Updated:</span>
                      <span className="value">{formatDate(selectedApplication.updated_at)}</span>
                    </div>
                  </div>
                </div>

                {/* Payment Information */}
                <div className="detail-section">
                  <h3>Payment Information</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="label">Payment Status:</span>
                      <span className={`value payment-badge ${selectedApplication.payment_status || 'none'}`}>
                        {getPaymentStatusIcon(selectedApplication.payment_status)}
                        {selectedApplication.payment_status_display || 'No Payment'}
                      </span>
                    </div>
                    {selectedApplication.payment_amount && (
                      <div className="detail-item">
                        <span className="label">Amount:</span>
                        <span className="value">₹{selectedApplication.payment_amount}</span>
                      </div>
                    )}
                    {selectedApplication.razorpay_payment_id && (
                      <div className="detail-item">
                        <span className="label">Payment ID:</span>
                        <span className="value">{selectedApplication.razorpay_payment_id}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Uploaded Files */}
                <div className="detail-section">
                  <h3>Uploaded Documents</h3>
                  <div className="files-grid">
                    {selectedApplication.video_file && (
                      <div className="file-item">
                        <div className="file-icon">
                          <FileText size={24} />
                        </div>
                        <div className="file-info">
                          <span className="file-name">Video Presentation</span>
                          <span className="file-type">MP4/Video File</span>
                        </div>
                        <a 
                          href={`${API_BASE_URL}/files/download/${selectedApplication.video_file}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="download-btn"
                        >
                          Download
                        </a>
                      </div>
                    )}
                    {selectedApplication.pdf_file && (
                      <div className="file-item">
                        <div className="file-icon">
                          <FileText size={24} />
                        </div>
                        <div className="file-info">
                          <span className="file-name">Business Plan</span>
                          <span className="file-type">PDF Document</span>
                        </div>
                        <a 
                          href={`${API_BASE_URL}/files/download/${selectedApplication.pdf_file}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="download-btn"
                        >
                          Download
                        </a>
                      </div>
                    )}
                    {!selectedApplication.video_file && !selectedApplication.pdf_file && (
                      <div className="no-files">
                        <FileText size={48} />
                        <p>No files uploaded</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Admin Notes */}
                {selectedApplication.admin_notes && (
                  <div className="detail-section">
                    <h3>Admin Notes</h3>
                    <div className="admin-notes">
                      <p>{selectedApplication.admin_notes}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedPayment && (
        <div className="modal-overlay" onClick={closePaymentModal}>
          <div className="modal-content payment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Make Payment</h2>
              <button className="modal-close" onClick={closePaymentModal}>
                <X size={24} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="payment-details">
                <div className="payment-summary">
                  <h3>Payment Summary</h3>
                  <div className="payment-amount-display">
                    <DollarSign size={32} />
                    <span className="amount">₹{selectedPayment.amount}</span>
                  </div>
                  <p className="payment-description">
                    Additional payment for application #{selectedPayment.application_id}
                  </p>
                </div>

                {selectedPayment.failure_reason && (
                  <div className="payment-reason">
                    <h4>Reason for Additional Payment:</h4>
                    <p>{selectedPayment.failure_reason}</p>
                  </div>
                )}

                <div className="payment-methods">
                  <h4>Payment Method</h4>
                  <p>You will be redirected to Razorpay to complete your payment securely.</p>
                </div>

                <div className="payment-actions">
                  <button 
                    className="cancel-btn"
                    onClick={closePaymentModal}
                    disabled={paymentLoading}
                  >
                    Cancel
                  </button>
                  <button 
                    className="pay-btn"
                    onClick={handlePaymentSubmit}
                    disabled={paymentLoading}
                  >
                    {paymentLoading ? (
                      <>
                        <Clock size={16} />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCard size={16} />
                        Pay ₹{selectedPayment.amount}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserPanel; 